import type { InvoicePreviewLineItem, LineItemTaxBreakdown } from "./types";

export type BillingFrequency =
  | "onetime"
  | "monthly"
  | "quarterly"
  | "halfyearly"
  | "yearly";

export const BILLING_FREQUENCY_OPTIONS: {
  value: BillingFrequency;
  label: string;
  months: number;
}[] = [
  { value: "onetime", label: "One Time", months: 0 },
  { value: "monthly", label: "Monthly", months: 1 },
  { value: "quarterly", label: "Quarterly", months: 3 },
  { value: "halfyearly", label: "Half-Yearly", months: 6 },
  { value: "yearly", label: "Yearly", months: 12 },
];

export function parseDateInputValue(value: string): Date | null {
  if (!value) {
    return null;
  }

  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) {
    return null;
  }

  return new Date(year, month - 1, day);
}

/** Format a Date as YYYY-MM-DD for `<input type="date" />`. */
export function formatDateInputValue(date: Date): string {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/** Add days to a YYYY-MM-DD date string; returns YYYY-MM-DD or empty string. */
export function addDaysToDateInput(value: string, days: number): string {
  const date = parseDateInputValue(value);
  if (!date) {
    return "";
  }
  date.setDate(date.getDate() + days);
  return formatDateInputValue(date);
}

export function formatPreviewDate(date: Date): string {
  const day = String(date.getDate()).padStart(2, "0");
  const month = date.toLocaleString("en-GB", { month: "short" });
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
}

export function getFrequencyMonths(frequency: BillingFrequency): number {
  return (
    BILLING_FREQUENCY_OPTIONS.find(option => option.value === frequency)
      ?.months ?? 6
  );
}

export function calculateBillingPeriodEnd(
  startDate: Date,
  frequency: BillingFrequency
): Date {
  // One-time billing has no end date; callers should treat period "to" as null.
  if (frequency === "onetime") {
    return new Date(startDate);
  }

  const endDate = new Date(startDate);
  endDate.setMonth(endDate.getMonth() + getFrequencyMonths(frequency));
  endDate.setDate(endDate.getDate() - 1);
  return endDate;
}

export function formatBillingPeriod(
  startDateValue: string,
  frequency: BillingFrequency
): string {
  const startDate = parseDateInputValue(startDateValue);
  if (!startDate) {
    return "—";
  }

  // One Time: show start date only (no end date)
  if (frequency === "onetime") {
    return formatPreviewDate(startDate);
  }

  const endDate = calculateBillingPeriodEnd(startDate, frequency);
  return `${formatPreviewDate(startDate)} – ${formatPreviewDate(endDate)}`;
}

export function buildPreviewSubtitle(
  _billingTypeSubtitle: string,
  frequencyLabel: string
): string {
  // Detail line shows "(period) · Frequency" — billing type name is not included
  return frequencyLabel?.trim() ?? "";
}

const LINE_ITEM_DETAIL_SEPARATOR = "\x1e";

export function formatLineItemSubtitleLine(
  subtitle: string,
  billingPeriod: string
): string {
  if (!subtitle?.trim() && !billingPeriod?.trim()) {
    return "";
  }

  if (!billingPeriod?.trim()) {
    return subtitle;
  }

  if (!subtitle?.trim()) {
    return billingPeriod;
  }

  return `${subtitle}${LINE_ITEM_DETAIL_SEPARATOR}${billingPeriod}`;
}

export function parseLineItemSubtitleLine(line: string): {
  subtitle: string;
  billingPeriod: string;
} {
  if (!line.includes(LINE_ITEM_DETAIL_SEPARATOR)) {
    return { subtitle: line, billingPeriod: "" };
  }

  const [subtitle, billingPeriod] = line.split(LINE_ITEM_DETAIL_SEPARATOR);
  return { subtitle, billingPeriod };
}

export function formatLineItemDescription(
  item: Pick<InvoicePreviewLineItem, "title" | "subtitle" | "billingPeriod">
): string {
  const detailLine = formatLineItemSubtitleLine(
    item.subtitle,
    item.billingPeriod
  );

  return [item.title, detailLine].filter(part => part?.trim()).join("\n");
}

export function parseLineItemDescription(description?: string): {
  title: string;
  subtitle: string;
  billingPeriod: string;
} {
  const text = description?.trim();
  if (!text) {
    return { title: "—", subtitle: "", billingPeriod: "" };
  }

  const parts = text
    .split(/\r?\n/)
    .map(part => part.trim())
    .filter(Boolean);
  const title = parts[0] ?? "—";

  if (parts.length < 2) {
    return { title, subtitle: "", billingPeriod: "" };
  }

  const { subtitle, billingPeriod } = parseLineItemSubtitleLine(parts[1]);
  return { title, subtitle, billingPeriod };
}

export function isInstateGstType(gstType?: string): boolean {
  return gstType?.trim().toLowerCase() !== "outstate";
}

export function calculateLineItemTax(
  amount: number,
  gstApplicable: boolean,
  gstRate: number,
  gstType?: string
): LineItemTaxBreakdown {
  const safeRate = Number(gstRate) || 0;
  const isInstate = isInstateGstType(gstType);

  if (!gstApplicable || safeRate <= 0) {
    return {
      cgst: 0,
      sgst: 0,
      igst: 0,
      taxTotal: 0,
      halfGstRate: 0,
      fullGstRate: 0,
      isInstate,
    };
  }

  const taxTotal = (amount * safeRate) / 100;

  if (isInstate) {
    return {
      cgst: taxTotal / 2,
      sgst: taxTotal / 2,
      igst: 0,
      taxTotal,
      halfGstRate: safeRate / 2,
      fullGstRate: safeRate,
      isInstate: true,
    };
  }

  return {
    cgst: 0,
    sgst: 0,
    igst: taxTotal,
    taxTotal,
    halfGstRate: 0,
    fullGstRate: safeRate,
    isInstate: false,
  };
}

export function getGstTaxRowCount(
  gstApplicable: boolean,
  gstType?: string
): number {
  if (!gstApplicable) {
    return 0;
  }

  return isInstateGstType(gstType) ? 2 : 1;
}

export function calculateInvoiceTotals(
  items: Array<{
    amount: number;
    gstApplicable: boolean;
    gstRate: number;
  }>,
  gstType?: string
) {
  return items.reduce(
    (totals, item) => {
      const tax = calculateLineItemTax(
        item.amount,
        item.gstApplicable,
        item.gstRate,
        gstType
      );
      totals.subtotal += item.amount;
      totals.taxTotal += tax.taxTotal;
      totals.total += item.amount + tax.taxTotal;
      return totals;
    },
    { subtotal: 0, taxTotal: 0, total: 0 }
  );
}

export function getBillingTypeRecordId(
  record: Record<string, unknown>
): string {
  const value =
    record.id ?? record.ID ?? record.Id ?? record._id ?? record.pk ?? "";
  return value !== null ? String(value).trim() : "";
}

export function extractBillingTypeSubtitle(
  record: Record<string, unknown> | undefined
): string {
  if (!record) {
    return "";
  }

  const directKeys = [
    "subtitle",
    "sub_title",
    "Subtitle",
    "subTitle",
    "billing_subtitle",
    "item_subtitle",
  ];

  for (const key of directKeys) {
    const value = record[key];
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }

  for (const [key, value] of Object.entries(record)) {
    if (
      key.toLowerCase().includes("subtitle") &&
      typeof value === "string" &&
      value.trim()
    ) {
      return value.trim();
    }
  }

  return "";
}
